<template>
  <div>
    <px-view-header title="Cadastro de solicitação"
                    title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1" icon x-large color="primary" @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>

    <div v-if="loadingForm">
      <px-circular-loading/>
    </div>
    <v-stepper class="elevation-1 rounded-card" v-if="!loadingForm" v-model="currentStep" vertical>

      <v-stepper-step :complete="currentStep > 1" step="1">Dados básicos</v-stepper-step>
      <v-stepper-content step="1">
        <px-cadastro-requisicao-dados-basicos :requisicao="requisicao"
                                              :is-edicao="isEdicao"
                                              :is-rascunho="isRascunho"
                                              :campos-obrigatorios="camposObrigatorios"
                                              @next="next"
                                              @cancel="onClickCancelar"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 2" step="2">Produtos</v-stepper-step>
      <v-stepper-content step="2">
        <px-cadastro-requisicao-produtos-table :requisicao="requisicao"
                                               :is-edicao="isEdicao"
                                               :campos-obrigatorios="camposObrigatorios"
                                               :campos-hidden="camposHidden"
                                               :erp-campos-integracao="erpCamposIntegracao"
                                               @next="next"
                                               @previous="previous"
                                               @reset-fornecedor="resetFornecedor"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 3" step="3">Sugestão de fornecedores</v-stepper-step>
      <v-stepper-content step="3">
        <px-cadastro-requisicao-fornecedores v-if="currentStep === 3"
                                             :requisicao="requisicao"
                                             :is-editing="isEditing"
                                             :is-edicao="isEdicao"
                                             :loading="loading"
                                             :loading-save-rascunho="loadingSaveRascunho"
                                             @save-draft="saveDraft"
                                             @previous="previous"
                                             @enviar-requisicao="enviarRequisicao"
                                             @reset-fornecedor="resetFornecedor"/>
      </v-stepper-content>
    </v-stepper>
    <px-confirm-dialog ref="confirm"/>
  </div>
</template>
<script>
import { cloneDeep } from 'lodash';
import { SituacaoRequisicao } from 'px-business-components';
import CompradorService from '@/api/comprador-service';
import CadastroRequisicaoDadosBasicos
  from '@/modules/requisicao/cadastro/CadastroRequisicaoDadosBasicos.vue';
import EntidadeCamposRequisicaoService
  from '@/modules/erp-campos/entidade-campos-requisicao-service';
import { mapGetters } from 'vuex';
import { getCadastroAdicional, buildCadastrosAdicionaisPayload } from '@/utils/cadastros-adicionais-utils';
import TiposEntidadeCadastroAdicional from '@/enums/tipos-entidade-cadastro-adicional';
import { deepNotEquals } from '@/utils/object-utils';
import EntidadeCamposPersonalizadosService from '@/modules/campos-personalizados/entidade-campos-personalizados-service';
import { setCamposPersonalizados } from '@/modules/campos-personalizados/campos-personalizado-utils';
import RequisicaoService from '../requisicoes-service';
import CadastroRequisicaoProdutosTable from './CadastroRequisicaoProdutosTable.vue';
import CadastroRequisicaoFornecedores from './CadastroRequisicaoFornecedores.vue';
import { hasCamposAdicionaisErp } from './campos-erp/validate-campos-erp';
import {
  initialState,
  computeInitialEdicao,
  computeInitialRascunho,
  computeInitialCopia,
} from './utils/requisicao-utils';

export default {
  data() {
    return {
      isRascunho: this.$route.params.isRascunho,
      isCopy: this.$route.params.isCopy,
      idRequisicao: this.$route.params.id,
      loadingForm: false,
      loading: false,
      loadingSaveRascunho: false,
      camposObrigatorios: null,
      camposHidden: null,
      camposPersonalizados: null,
      requisicao: null,
      initialRequisicao: {},
      breadcrumbs: [
        { text: 'Gestão de compras' },
        {
          text: 'Solicitações',
          to: '/requisicoes',
        },
        { text: 'Cadastro de solicitação' },
      ],
      currentStep: 1,
    };
  },
  computed: {
    ...mapGetters('entidade', ['erpCamposIntegracao', 'idEntidade']),
    confirmMessage() {
      return this.isRascunho ? 'Você deseja atualizar este rascunho?' : 'Você deseja salvar esta solicitação como rascunho?';
    },
    isEdicao() {
      return this.idRequisicao && !this.isRascunho && !this.isCopy;
    },
    isEditing() {
      return this.isEdicao || this.isRascunho;
    },
    isFormDirty() {
      if (this.loadingForm) {
        return false;
      }
      return Object.keys(this.initialRequisicao)
        .some(key => deepNotEquals(this.requisicao, this.initialRequisicao, key));
    },
  },
  methods: {
    beforeWindowUnload(e) {
      if (this.isFormDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    next() {
      this.currentStep = this.currentStep + 1;
    },
    previous() {
      this.currentStep = this.currentStep - 1;
    },
    getCadastroAdicionalProduto(produto, tipo) {
      if (!produto || !produto.cadastrosAdicionais) {
        return {};
      }
      return getCadastroAdicional(produto.cadastrosAdicionais, tipo);
    },
    buildCadastrosAdicionaisProdutoPayload(produto) {
      if (!produto.cadastrosAdicionais
        || !hasCamposAdicionaisErp(this.erpCamposIntegracao, produto.cadastrosAdicionais)) {
        return [];
      }
      return buildCadastrosAdicionaisPayload(
        produto.cadastrosAdicionais,
        TiposEntidadeCadastroAdicional.keys.REQUISICAO_PRODUTO.key,
      );
    },
    buildRequisicaoBody() {
      const requisicao = { ...this.requisicao };
      requisicao.produtos = requisicao.produtos
        .map(produto => ({
          ...produto,
          cadastrosAdicionais: this.buildCadastrosAdicionaisProdutoPayload(produto),
        }));
      return requisicao;
    },
    buildRequisicaoDraft() {
      const requisicao = { ...this.requisicao };
      requisicao.projeto = requisicao.projeto || null;
      requisicao.centroCusto = requisicao.centroCusto || null;
      requisicao.urgencia = requisicao.urgencia || null;
      requisicao.situacao = SituacaoRequisicao.keys.RASCUNHO.key;
      requisicao.produtos = requisicao.produtos
        .filter(produto => !!produto.unidadeMedida)
        .map(produto => ({
          ...produto,
          situacao: SituacaoRequisicao.keys.RASCUNHO.key,
          cadastrosAdicionais: this.buildCadastrosAdicionaisProdutoPayload(produto),
        }));
      return requisicao;
    },
    onClickBack() {
      if (this.isEdicao) {
        this.$router.push({ name: 'main.requisicoes' });
        return;
      }
      this.$refs.confirm.open(this.confirmMessage,
        'O rascunho ficara disponível na aba RASCUNHOS e TODAS da listagem de solicitações.',
        { width: 450 })
        .then((confirm) => {
          if (!confirm) {
            this.$router.push({ name: 'main.requisicoes' });
            return;
          }
          this.saveDraft();
        });
    },
    onClickCancelar() {
      this.$router.push({ name: 'main.requisicoes' });
    },
    resetFornecedor() {
      this.requisicao.fornecedores = [];
    },
    enviarRequisicao() {
      this.loading = true;

      const isUpdate = !!this.idRequisicao && !this.isCopy && !this.isRascunho;
      const message = isUpdate ? 'Requisição atualizada com sucesso' : 'Requisição enviada com sucesso';

      RequisicaoService.save(this.buildRequisicaoBody())
        .then(({ data }) => {
          this.$toast(message, { color: 'success' });
          if (data.aprovacao) {
            this.$router.push({ name: 'main.aprovacoes-solicitacoes' });
            return;
          }
          this.$router.push({ name: 'main.requisicoes' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveDraft() {
      this.loadingSaveRascunho = true;

      const updateRascunho = !!this.idRequisicao && this.isRascunho;
      const message = updateRascunho ? 'Rascunho atualizado com sucesso' : 'Rascunho salvo com sucesso';

      RequisicaoService.saveRascunho(this.buildRequisicaoDraft())
        .then(() => {
          this.$toast(message, { color: 'success' });
          this.$router.push({ name: 'main.requisicoes' });
        })
        .finally(() => {
          this.loadingSaveRascunho = false;
        });
    },
    create(data) {
      if (this.isCopy) {
        this.requisicao = computeInitialCopia(data, this.erpCamposIntegracao);
        this.setInitialRequisicao();
        return;
      }
      if (this.isRascunho) {
        this.requisicao = computeInitialRascunho(data, this.erpCamposIntegracao);
        this.setInitialRequisicao();
        return;
      }
      this.requisicao = computeInitialEdicao(data, this.erpCamposIntegracao);
      this.setInitialRequisicao();
    },
    getRequisicao() {
      this.loadingForm = true;
      RequisicaoService.getRequisicao(this.idRequisicao)
        .then((response) => {
          this.create(response.data);
        })
        .finally(() => {
          this.loadingForm = false;
        });
    },
    getCompradorDefaultFaturamento() {
      if (this.requisicao.comprador) {
        return;
      }
      CompradorService.getDefaultFaturamento()
        .then((response) => {
          this.requisicao.comprador = response.data;
        });
    },
    getCompradorDefaultEntrega() {
      if (this.requisicao.locaisEntrega) {
        return;
      }
      CompradorService.getDefaultEntrega()
        .then((response) => {
          this.requisicao.locaisEntrega = response.data;
        });
    },
    getCamposObrigatorios() {
      EntidadeCamposRequisicaoService.getCamposObrigatorios(this.erpCamposIntegracao)
        .then((data) => {
          this.camposObrigatorios = data;
        });
    },
    getCamposHidden() {
      EntidadeCamposRequisicaoService.getCamposHidden(this.erpCamposIntegracao)
        .then((data) => {
          this.camposHidden = data;
        });
    },
    setInitialRequisicao() {
      this.initialRequisicao = cloneDeep(this.requisicao);
    },
    getCamposPersonalizados() {
      EntidadeCamposPersonalizadosService.getCamposPersonalizadosRequisicaoById(this.idEntidade)
        .then((campos) => {
          this.camposPersonalizados = campos;
          this.requisicao = setCamposPersonalizados(this.requisicao, this.camposPersonalizados);
        });
    },
  },
  created() {
    window.addEventListener('beforeunload', this.beforeWindowUnload);

    this.requisicao = { ...initialState(this.erpCamposIntegracao) };

    if (this.idRequisicao) {
      this.loadingForm = true;
      Promise.all([
        this.getCamposHidden(),
        this.getCamposPersonalizados(),
        this.getCamposObrigatorios(),
        this.getCompradorDefaultEntrega(),
        this.getCompradorDefaultFaturamento(),
        this.getRequisicao(),
      ]).finally(() => {
        this.loadingForm = false;
      });
      return;
    }

    this.loadingForm = true;

    Promise.all([
      this.getCamposHidden(),
      this.getCamposObrigatorios(),
      this.getCamposPersonalizados(),
      this.getCompradorDefaultEntrega(),
      this.getCompradorDefaultFaturamento(),
    ])
      .finally(() => {
        this.setInitialRequisicao();
        this.loadingForm = false;
      });
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },
  components: {
    pxCadastroRequisicaoDadosBasicos: CadastroRequisicaoDadosBasicos,
    pxCadastroRequisicaoProdutosTable: CadastroRequisicaoProdutosTable,
    pxCadastroRequisicaoFornecedores: CadastroRequisicaoFornecedores,
  },
};
</script>
